import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"

export function useLetter(formData: any) {
  const [letter, setLetter] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)
  const [letterSource, setLetterSource] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const { toast } = useToast()

  const generateLetter = async (userFeedback?: string) => {
    setIsLoading(true)
    setErrorMessage(null)
    setErrorDetails(null)
    setLetterSource(null)
    
    try {
      console.log('Generating letter with formData:', formData)
      
      // Set up a client-side timeout that's longer than the server-side one
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30-second timeout
      
      // Create a fetch request with timeout capability 
      // but don't pass the signal to the function invoke options
      const { data, error } = await supabase.functions.invoke('generate-letter', {
        body: { 
          formData,
          feedback: userFeedback 
        }
      })
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId)

      if (error) {
        console.error('Supabase function error:', error)
        throw error
      }

      console.log('Generated letter response:', data)
      
      if (!data || !data.letter) {
        throw new Error('No letter content received from API')
      }
      
      setLetter(data.letter)
      setLetterSource(data.source || null)
      
      if (data.source === 'fallback' || data.source === 'error') {
        setErrorMessage(data.source === 'fallback' 
          ? "We couldn't generate a custom letter, so we've provided a template you can edit." 
          : "There was an error generating your letter. We've provided a template for you to edit.")
        
        if (data.error) {
          setErrorDetails(data.error)
        }
      }
    } catch (error: any) {
      console.error("Error generating letter:", error)
      
      // Special handling for timeout errors
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        setErrorMessage("The letter generation timed out. Please try again or use the template provided.")
        setErrorDetails("The AI service took too long to respond. This could be due to high traffic or complexity of the request.")
      } else {
        setErrorMessage("Failed to generate letter. We've provided a basic template that you can edit.")
        setErrorDetails(error.message || "Unknown error occurred")
      }
      
      toast({
        variant: "destructive",
        title: "Letter Generation Issue",
        description: "Our AI is taking longer than expected. You can edit the provided template or try regenerating.",
      })
      
      const today = new Date().toLocaleDateString('en-US', { 
        month: 'long', day: 'numeric', year: 'numeric' 
      })
      
      const isAnonymous = formData.sender.isAnonymous === true || formData.sender.isAnonymous === "true"
      const senderName = isAnonymous ? "A Concerned Citizen" : `${formData.sender.firstName} ${formData.sender.lastName}`
      
      const fallbackLetter = `CEASE AND DESIST

${today}

TO: ${formData.recipient.firstName} ${formData.recipient.lastName}

FROM: ${senderName}

RE: A Friendly But Firm Request to Stop What You're Doing

This letter serves as my formal (but good-humored) demand that you immediately CEASE AND DESIST the following actions:

${formData.situation.description}

While I appreciate creativity in all its forms, this particular situation has crossed the line from "innovative" to "legally problematic." I must insist these actions stop immediately.

You have 7 days from receipt of this letter to confirm your compliance. That's 168 hours, 10,080 minutes, or just enough time to binge-watch several legal dramas that might help you understand the seriousness of this situation.

Should you choose to ignore this remarkably reasonable request, I'll be forced to pursue all available legal remedies - a path that would be considerably less amusing for both of us.

With all due respect and a dash of wit,

${senderName}`
      
      setLetter(fallbackLetter)
      setLetterSource('error-fallback')
    } finally {
      setIsLoading(false)
    }
  }

  const retryGeneration = () => {
    setRetryCount(prev => prev + 1)
    setIsLoading(true)
    generateLetter()
  }

  return {
    letter,
    setLetter,
    isLoading,
    errorMessage,
    errorDetails,
    letterSource,
    retryCount,
    generateLetter,
    retryGeneration
  }
}
